import React, { useCallback, useRef } from 'react';

/**
 * Искусственная задержка перед вызовом коллбека.
 *
 * @param callback
 * @param delay
 * @returns {function}
 *
 * @example
 * const updateValue = (value) => { ... }; // например, где-то сверху прилетает
 * 
 * // например, в компоненте поиска
 * const delayedUpdateValue = useDebounce(updateValue, debounceDelay);
 * const onChange = () => {
 * ...
 *  delayedUpdateValue(inputValue);
 * }
 */
export function useDebounce(callback, delay = 300) {
  const timer = useRef();

  /*
    делаем таймаут и в случае, если функция вызвалась еще раз,
    то таймаут удаляется и записывается новый
    когда прекращается, например, ввод, то вызывается колбек
   */
  const debouncedCallback = useCallback((...args) => {
    if (timer.current) {
      clearTimeout(timer.current);
    }

    timer.current = setTimeout(() => {
      callback(...args);
    }, delay);
  }, [callback, delay]);

  return debouncedCallback;
}

/**
 * Искусственная задержка на изменение значения.
 *
 * @param value
 * @param {number} delay
 * @returns debouncedValue от value
 *
 * @example
 * const [inputValue, setInputValue] = useState('')
 * // debouncedValue используем там где нужно, например для фильтрации списка
 * const debouncedValue = useDebounceValue(inputValue, 350)
 */
export function useDebounceValue(value, delay = 300) {
  const [debouncedValue, setDebouncedValue] = React.useState(value);

  React.useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
}
